import { useTheme } from '@emotion/react'
import { MapToOptions } from './MappingForms/MapToOptions'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { MapToHomekareForm } from './MappingForms/MapToHomekareForm'
import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { selectAddDeviceCount, setPatientInfo } from './ProrithmSlice'
import { getAllProrithmDevices } from '../../services/ProrithmServices'
import {
  messages,
  constants,
  DeviceMappingData,
  paginationOptions,
  ProrithmDeviceList,
} from './Constants'
import {
  Box,
  Dialog,
  Backdrop,
  Typography,
  IconButton,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material'
import {
  ColDef,
  CellClickedEvent,
  ICellRendererParams,
} from 'ag-grid-community'
import useProrithmSocket from './useProrithmSocket'
import ProRithmAgDataGrid from './ProRithmAgDataGrid'
import DeleteDeviceDialog from './DeleteDeviceDialog'
import MapToIpdForm from './MappingForms/MapToIpdForm'
import DeviceMappingStatus from './DeviceMappingStatus'
import DeviceMappingDetails from './DeviceMappingDetails'
import DeviceMappingActions from './DeviceMappingActions'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'
import MapToLocationForm from './MappingForms/MapToLocationForm'
import MapToAmbulanceForm from './MappingForms/MapToAmbulanceForm'
import RemapSmartRing from './RemapSmartRing'

export const styles = () => ({
  proRithmDasboardContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    width: '100%',
    padding: '2%',
    gap: 20,
  },
  proRithmDasboardheading: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  proRithmDasboardCreateBtn: {
    textTransform: 'capitalize',
    backgroundColor: '#4682b4',
  },
  deviceText: {
    color: '#090968',
    fontSize: '16px',
    fontWeight: 600,
  },
  centerHeader: {
    color: '#ffffff',
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
    },
  },
  selectDropdown: {
    width: '210px',
  },
  selectGender: {
    width: '210px',
    marginLeft: '10px',
    marginRight: '10px',
    marginTop: '10px',
  },
  textField: {
    marginLeft: '10px',
    marginRight: '10px',
    marginTop: '10px',
  },
  reportsTable: {
    '& th, & td': {
      border: '1px solid rgba(224, 224, 224, 1)',
    },
  },
})

const ProRithmDasboard = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const dispatch = useAppDispatch()
  const addDeviceCount = useAppSelector(selectAddDeviceCount)
  const {
    createProrithmWebsocket,
    sendMessageOnSocket,
    closeProrithmWebSocket,
  } = useProrithmSocket()

  const dummyMessage = {
    messageType: constants?.heartbeat,
    messageContent: {
      message: '',
      macId: '',
      category: '',
    },
  }

  const [prorithmDeviceList, setProrithmDeviceList] = useState<
    ProrithmDeviceList[]
  >([])
  const [isDevicesLoading, setIsDevicesLoading] = useState<boolean>(false)
  const [categoryType, setCategoryType] = useState<string | null>(null)
  const [page, setPage] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(20)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [openAddToDialog, setOpenAddToDialog] = useState<boolean>(false)
  const [mapTo, setMapTo] = useState<string>('')
  const [mapToPatient, setMapToPatient] = useState<string>('')
  const [mappingId, setMappingId] = useState<string | null>(null)
  const [openDeleteDeviceDialog, setOpenDeleteDeviceDialog] =
    useState<boolean>(false)
  const [openEditDeviceDialog, setOpenEditDeviceDialog] =
    useState<boolean>(false)
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
  const [openMappingDetails, setOpenMappingDetails] = useState<boolean>(false)
  const [deviceMappingDetails, setDeviceMappingDetails] =
    useState<DeviceMappingData | null>(null)
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>('')
  const [openRemapDialog, setOpenRemapDialog] = useState<boolean>(false)
  const [isRemapEditMode, setIsRemapEditMode] = useState<boolean>(false)

  const handleEditDevice = (data: ProrithmDeviceList) => {
    setSelectedDeviceId(data?.deviceId ?? '')
    setMappingId(data?.mappingId ?? null)
    setCategoryType(data?.categoryType)

    if (data?.deviceType === constants?.RING) {
      setIsRemapEditMode(true)
      setOpenRemapDialog(true)
    } else {
      setOpenEditDeviceDialog(true)
    }
  }

  const handleDeleteDevice = (data: ProrithmDeviceList) => {
    setOpenDeleteDeviceDialog(true)
    setSelectedDeviceId(data?.deviceId ?? '')
    setMappingId(data?.mappingId ?? null)
  }

  const columnDefs: ColDef[] = [
    {
      headerName: 'S No',
      field: 'sNo',
      flex: 1,
      sortable: true,
      filter: false,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Device Type',
      field: 'deviceType',
      minWidth: 150,
      flex: 1,
      sortable: true,
      filter: false,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
    },
    {
      headerName: 'Device ID',
      field: 'deviceId',
      minWidth: 150,
      sortable: true,
      flex: 1,
      filter: false,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textDecoration: 'underline',
        cursor: 'pointer',
      },
      onCellClicked: (event: CellClickedEvent) => {
        handleDeviceClick(event)
      },
    },
    {
      headerName: 'Patient ID',
      field: 'patientId',
      minWidth: 150,
      sortable: true,
      flex: 1,
      filter: false,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
    },
    {
      headerName: 'Category',
      field: 'categoryType',
      minWidth: 150,
      flex: 1,
      sortable: true,
      filter: false,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px' },
    },
    {
      headerName: 'Mapping Details',
      field: 'mappingDetails',
      minWidth: 150,
      flex: 1,
      sortable: false,
      filter: false,
      cellClass: 'custom-header-cell',
      headerClass: 'custom-header-cell',
      cellRenderer: (params: any) => {
        return (
          <IconButton
            size='small'
            onClick={() => getDeviceMappingDetails(params?.data)}
          >
            <InfoOutlinedIcon fontSize='small' />
          </IconButton>
        )
      },
      cellStyle: {
        fontSize: '12px',
        alignItems: 'center',
        textAlign: 'center',
      },
    },
    {
      headerName: 'Actions',
      field: 'actions',
      width: 200,
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <DeviceMappingActions
            params={params}
            handleEditDevice={handleEditDevice}
            handleDeleteDevice={handleDeleteDevice}
          />
        )
      },
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
  ]

  const getDeviceMappingDetails = (data: DeviceMappingData) => {
    setDeviceMappingDetails(data)
    setOpenMappingDetails(true)
  }

  const handleDeviceClick = async (event: CellClickedEvent) => {
    setSelectedDeviceId(event?.data?.deviceId ?? '')
    setCategoryType(event?.data?.categoryType)
    setMappingId(event?.data?.mappingId ?? null)

    if (event?.data?.deviceType === constants?.RING) {
      setIsRemapEditMode(false)
      setOpenRemapDialog(true)
    } else {
      setOpenAddToDialog(true)
    }
  }

  const handleMapToChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMapTo((event.target as HTMLInputElement).value)
  }

  const handleMappingDialogClose = () => {
    if (openAddToDialog) {
      setOpenAddToDialog(false)
    }
    if (openEditDeviceDialog) {
      setOpenEditDeviceDialog(false)
    }
    setMapTo('')
    setMappingId(null)
    setMapToPatient('')
    setSelectedDeviceId('')
    dispatch(setPatientInfo(null))
  }

  const handleEditMappingDialogClose = () => {
    setMapTo('')
    setMappingId(null)
    setMapToPatient('')
    setSelectedDeviceId('')
    dispatch(setPatientInfo(null))
    setOpenEditDeviceDialog(false)
  }

  const handleRemapDialogClose = () => {
    setOpenRemapDialog(false)
    setSelectedDeviceId('')
    setMappingId(null)
    setCategoryType(null)
    setIsRemapEditMode(false)
    dispatch(setPatientInfo(null))
  }

  const fetchProrithmDevices = useCallback(async () => {
    setIsDevicesLoading(true)
    try {
      const devices = await getAllProrithmDevices(
        page,
        pageSize,
        deviceTypeFilter
      )

      if (devices && devices?.data) {
        const result = devices?.data?.content?.map(
          (item: any, index: number) => ({
            sNo: page * pageSize + index + 1,
            ...item,
          })
        )

        setProrithmDeviceList(result)
        setTotalCount(devices?.data?.totalElements)
        setPage(devices?.data?.pageable?.pageNumber)
        setPageSize(devices?.data?.pageable?.pageSize)
      } else {
        console.error(messages?.getAllDevicesError)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsDevicesLoading(false)
    }
  }, [page, pageSize, deviceTypeFilter])

  const CategoryMappingForm = ({ type }: { type: string }) => {
    const mappingForm = (
      <>
        <Typography>{constants?.category}:</Typography>
        <MapToOptions mapTo={mapTo} handleMapToChange={handleMapToChange} />
        {mapTo === constants?.location && (
          <MapToLocationForm
            mapTo={mapTo}
            mappingId={mappingId}
            categoryType={categoryType}
            selectedDeviceId={selectedDeviceId}
            handleClose={handleMappingDialogClose}
            sendMessageOnSocket={sendMessageOnSocket}
          />
        )}
        {mapTo === constants?.ipd && (
          <MapToIpdForm
            mapTo={mapTo}
            mappingId={mappingId}
            categoryType={categoryType}
            selectedDeviceId={selectedDeviceId}
            handleClose={handleMappingDialogClose}
            sendMessageOnSocket={sendMessageOnSocket}
          />
        )}
        {mapTo === constants?.ambulanceText && (
          <MapToAmbulanceForm
            mapTo={mapTo}
            mappingId={mappingId}
            categoryType={categoryType}
            selectedDeviceId={selectedDeviceId}
            handleClose={handleMappingDialogClose}
            sendMessageOnSocket={sendMessageOnSocket}
          />
        )}
        {mapTo === constants?.homekare && (
          <MapToHomekareForm
            mapTo={mapTo}
            mappingId={mappingId}
            categoryType={categoryType}
            selectedDeviceId={selectedDeviceId}
            handleClose={handleMappingDialogClose}
            sendMessageOnSocket={sendMessageOnSocket}
          />
        )}
      </>
    )

    if (type === constants?.addMapping) {
      return !categoryType ? mappingForm : null
    } else if (type === constants?.editMapping) {
      return mappingForm
    }

    return !categoryType ? mappingForm : null
  }

  useEffect(() => {
    createProrithmWebsocket()
    const dummyMessageInterval = setInterval(() => {
      sendMessageOnSocket(dummyMessage)
    }, Number(process.env.REACT_APP_PRORITHM_SOCKET_DELAY))

    return () => {
      closeProrithmWebSocket()
      clearInterval(dummyMessageInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(setPatientInfo(null))
    if (
      mapTo === constants?.homekare &&
      mapToPatient === constants?.unidentified
    ) {
      setMapToPatient('')
    }
  }, [mapTo, dispatch, mapToPatient])

  useEffect(() => {
    // get all prorithm devices list
    const fetchAllDevicesTimeout = setTimeout(() => {
      fetchProrithmDevices()
    }, Number(process.env.REACT_APP_API_DELAY_TIMER))
    return () => {
      clearTimeout(fetchAllDevicesTimeout)
    }
  }, [page, pageSize, addDeviceCount, fetchProrithmDevices])

  return (
    <Box height='93%' width='100%'>
      <Dialog
        fullWidth
        maxWidth='md'
        open={openAddToDialog}
        onClose={handleMappingDialogClose}
      >
        <DialogTitle>{constants?.mapDevice}</DialogTitle>
        <DialogContent>
          <CategoryMappingForm type={constants?.addMapping} />
          <DeviceMappingStatus
            categoryType={categoryType}
            handleClose={handleMappingDialogClose}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth='md'
        open={openEditDeviceDialog}
        onClose={handleEditMappingDialogClose}
      >
        <DialogTitle>{constants?.editMappedDevice}</DialogTitle>
        <DialogContent>
          <CategoryMappingForm type={constants?.editMapping} />
        </DialogContent>
      </Dialog>
      <DeleteDeviceDialog
        open={openDeleteDeviceDialog}
        setOpen={setOpenDeleteDeviceDialog}
        mappingId={mappingId}
        deviceId={selectedDeviceId}
        sendMessageOnSocket={sendMessageOnSocket}
      />
      <DeviceMappingDetails
        open={openMappingDetails}
        setOpen={setOpenMappingDetails}
        deviceMappingDetails={deviceMappingDetails}
      />
      <RemapSmartRing
        open={openRemapDialog}
        deviceId={selectedDeviceId}
        mappingId={mappingId}
        categoryType={categoryType}
        isEditMode={isRemapEditMode}
        onClose={handleRemapDialogClose}
        sendMessageOnSocket={sendMessageOnSocket}
      />
      <Box
        className={classes?.proRithmDasboardContainer}
        height={'100%'}
        width={'100%'}
        position='relative'
      >
        <ProRithmAgDataGrid
          tableHeading='Device Mappings'
          tableHeight={'100%'}
          rowData={prorithmDeviceList}
          columnDefs={columnDefs}
          pagination={true}
          pageSizeOptions={paginationOptions}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRows={totalCount}
          headerHeight={30}
          searchFieldWidth={200}
          sendMessageOnSocket={sendMessageOnSocket}
          deviceTypeFilter={deviceTypeFilter}
          setDeviceTypeFilter={setDeviceTypeFilter}
        />
        <Backdrop open={isDevicesLoading}>
          <Typography color='white' variant='h6'>
            {constants?.loading}&nbsp;
            <CircularProgress color='inherit' />
          </Typography>
        </Backdrop>
      </Box>
    </Box>
  )
}

export default ProRithmDasboard
